import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { BlockMath } from "react-katex";
import { MathfieldElement } from "mathlive";
import "katex/dist/katex.min.css";

if (!customElements.get("math-field")) customElements.define("math-field", MathfieldElement);

const QUESTIONS = [
  {
    "id": "q1",
    "instruction": "These are the goals scored by 15 hockey teams. Make an ordered stem-and-leaf diagram.",
    "data": [
      21,
      30,
      26,
      37,
      34,
      21,
      24,
      45,
      23,
      20,
      15,
      23,
      18,
      13,
      19
    ],
    "parts": [],
    "calculator": "no",
    "draw": true,
    "guided": true,
    "unit": "goals",
    "rows": [
      [
        1,
        [
          3,
          5,
          8,
          9
        ]
      ],
      [
        2,
        [
          0,
          1,
          1,
          3,
          3,
          4,
          6
        ]
      ],
      [
        3,
        [
          0,
          4,
          7
        ]
      ],
      [
        4,
        [
          5
        ]
      ]
    ],
    "keyValue": 21,
    "displayAnswer": "\\begin{array}{r|l}1 & 3\\;5\\;8\\;9 \\\\ 2 & 0\\;1\\;1\\;3\\;3\\;4\\;6 \\\\ 3 & 0\\;4\\;7 \\\\ 4 & 5\\end{array}\\quad\\text{Key: }2|1=21\\quad",
    "answers": []
  },
  {
    "id": "q2",
    "instruction": "Make an ordered stem-and-leaf diagram for these 14 adults' ages.",
    "data": [
      42,
      35,
      56,
      39,
      40,
      51,
      47,
      38,
      42,
      55,
      42,
      48,
      49,
      41
    ],
    "parts": [],
    "calculator": "no",
    "draw": true,
    "guided": true,
    "unit": "years",
    "rows": [
      [
        3,
        [
          5,
          8,
          9
        ]
      ],
      [
        4,
        [
          0,
          1,
          2,
          2,
          2,
          7,
          8,
          9
        ]
      ],
      [
        5,
        [
          1,
          5,
          6
        ]
      ]
    ],
    "keyValue": 42,
    "displayAnswer": "\\begin{array}{r|l}3 & 5\\;8\\;9 \\\\ 4 & 0\\;1\\;2\\;2\\;2\\;7\\;8\\;9 \\\\ 5 & 1\\;5\\;6\\end{array}\\quad\\text{Key: }4|2=42\\quad",
    "answers": []
  },
  {
    "id": "q3",
    "instruction": "Helen's darts scores are listed below.\n(a) Make an ordered stem-and-leaf diagram.",
    "data": [
      55,
      23,
      48,
      29,
      41,
      47,
      36,
      35,
      40,
      35,
      44,
      34,
      35
    ],
    "parts": [
      {
        "id": "b",
        "label": "(b) Mode",
        "answer": "35"
      },
      {
        "id": "c",
        "label": "(c) Range",
        "answer": "32"
      }
    ],
    "calculator": "no",
    "draw": true,
    "unit": "points",
    "rows": [
      [
        2,
        [
          3,
          9
        ]
      ],
      [
        3,
        [
          4,
          5,
          5,
          5,
          6
        ]
      ],
      [
        4,
        [
          0,
          1,
          4,
          7,
          8
        ]
      ],
      [
        5,
        [
          5
        ]
      ]
    ],
    "keyValue": 55,
    "displayAnswer": "\\begin{array}{r|l}2 & 3\\;9 \\\\ 3 & 4\\;5\\;5\\;5\\;6 \\\\ 4 & 0\\;1\\;4\\;7\\;8 \\\\ 5 & 5\\end{array}\\quad\\text{Key: }5|5=55\\quad\\text{b: 35; c: 32}",
    "answers": []
  },
  {
    "id": "q4",
    "instruction": "Use these 11 footballers' game ratings to answer each part.",
    "data": [
      68,
      75,
      75,
      77,
      83,
      84,
      86,
      88,
      89,
      90,
      91
    ],
    "parts": [
      {
        "id": "a",
        "label": "(a) How many ratings are below 85?",
        "answer": "6"
      },
      {
        "id": "b",
        "label": "(b) Mode",
        "answer": "75"
      },
      {
        "id": "c",
        "label": "(c) Range",
        "answer": "23"
      },
      {
        "id": "d",
        "label": "(d) Median",
        "answer": "84"
      }
    ],
    "calculator": "no",
    "keyValue": 68,
    "rows": [
      [
        6,
        [
          8
        ]
      ],
      [
        7,
        [
          5,
          5,
          7
        ]
      ],
      [
        8,
        [
          3,
          4,
          6,
          8,
          9
        ]
      ],
      [
        9,
        [
          0,
          1
        ]
      ]
    ],
    "displayAnswer": "\\text{a: 6; b: 75; c: 23; d: 84}",
    "answers": []
  },
  {
    "id": "q5",
    "instruction": "These are 15 earthworms' lengths in millimetres.\n(a) Complete an ordered stem-and-leaf diagram.",
    "data": [
      43,
      19,
      35,
      28,
      21,
      28,
      28,
      18,
      24,
      33,
      31,
      36,
      12,
      41,
      28
    ],
    "parts": [
      {
        "id": "b",
        "label": "(b) How many lengths exceed 40 mm?",
        "answer": "2"
      },
      {
        "id": "c",
        "label": "(c) Mode (mm)",
        "answer": "28"
      },
      {
        "id": "d",
        "label": "(d) Fraction below 20 mm",
        "answer": "1/5"
      }
    ],
    "calculator": "no",
    "draw": true,
    "unit": "mm",
    "rows": [
      [
        1,
        [
          2,
          8,
          9
        ]
      ],
      [
        2,
        [
          1,
          4,
          8,
          8,
          8,
          8
        ]
      ],
      [
        3,
        [
          1,
          3,
          5,
          6
        ]
      ],
      [
        4,
        [
          1,
          3
        ]
      ]
    ],
    "keyValue": 43,
    "displayAnswer": "\\begin{array}{r|l}1 & 2\\;8\\;9 \\\\ 2 & 1\\;4\\;8\\;8\\;8\\;8 \\\\ 3 & 1\\;3\\;5\\;6 \\\\ 4 & 1\\;3\\end{array}\\quad\\text{Key: }4|3=43\\quad\\text{b: 2; c: 28; d: 1/5}",
    "answers": []
  },
  {
    "id": "q6",
    "instruction": "Kaitlyn recorded weekly cups of coffee. Identify two different mistakes in her diagram.",
    "data": [
      13,
      18,
      4,
      22,
      19,
      10,
      25,
      31,
      13,
      12
    ],
    "parts": [
      {
        "id": "a",
        "label": "Mistake 1",
        "answer": "order",
        "options": [
          "The leaves are not ordered",
          "One of the 13s is missing",
          "The stems are in the wrong order",
          "The key is incorrect"
        ]
      },
      {
        "id": "b",
        "label": "Mistake 2",
        "answer": "missing",
        "options": [
          "The leaves are not ordered",
          "One of the 13s is missing",
          "The stems are in the wrong order",
          "The key is incorrect"
        ]
      }
    ],
    "calculator": "no",
    "diagram": [
      [
        0,
        [
          4
        ]
      ],
      [
        1,
        [
          2,
          3,
          8,
          9,
          0
        ]
      ],
      [
        2,
        [
          2,
          5
        ]
      ],
      [
        3,
        [
          1
        ]
      ]
    ],
    "keyValue": 4,
    "mistakes": true,
    "rows": [
      [
        0,
        [
          4
        ]
      ],
      [
        1,
        [
          2,
          3,
          8,
          9,
          0
        ]
      ],
      [
        2,
        [
          2,
          5
        ]
      ],
      [
        3,
        [
          1
        ]
      ]
    ],
    "displayAnswer": "\\text{Leaves out of order; One 13 missing}",
    "answers": []
  },
  {
    "id": "q7",
    "instruction": "These are daily visitor counts for a cafe.\n(a) Complete the ordered diagram using the given key.",
    "data": [
      104,
      131,
      120,
      115,
      109,
      124,
      128,
      118,
      116,
      120,
      125
    ],
    "parts": [
      {
        "id": "b",
        "label": "(b) Mode",
        "answer": "120"
      },
      {
        "id": "c",
        "label": "(c) Median",
        "answer": "120"
      },
      {
        "id": "d",
        "label": "(d) Range",
        "answer": "27"
      }
    ],
    "calculator": "no",
    "draw": true,
    "keyProvided": true,
    "keyValue": 104,
    "rows": [
      [
        10,
        [
          4,
          9
        ]
      ],
      [
        11,
        [
          5,
          6,
          8
        ]
      ],
      [
        12,
        [
          0,
          0,
          4,
          5,
          8
        ]
      ],
      [
        13,
        [
          1
        ]
      ]
    ],
    "displayAnswer": "\\begin{array}{r|l}10 & 4\\;9 \\\\ 11 & 5\\;6\\;8 \\\\ 12 & 0\\;0\\;4\\;5\\;8 \\\\ 13 & 1\\end{array}\\quad\\text{Key: }10|4=104\\quad\\text{b: 120; c: 120; d: 27}",
    "answers": []
  },
  {
    "id": "q8",
    "instruction": "The stem and leaf diagram shows information about the time taken by each student to complete a puzzle.",
    "data": [
      23,
      25,
      26,
      28,
      28,
      28,
      29,
      30,
      32,
      33,
      36,
      37,
      41,
      44,
      58
    ],
    "parts": [
      {
        "id": "a",
        "label": "Is Thea correct?",
        "answer": "No",
        "options": [
          "Yes",
          "No"
        ]
      },
      {
        "id": "explanation",
        "label": "Explain your answer",
        "answer": "The probability is 12/15 = 4/5.",
        "explanation": true
      }
    ],
    "calculator": "no",
    "keyValue": 23,
    "unit": "minutes",
    "rows": [
      [
        2,
        [
          3,
          5,
          6,
          8,
          8,
          8,
          9
        ]
      ],
      [
        3,
        [
          0,
          2,
          3,
          6,
          7
        ]
      ],
      [
        4,
        [
          1,
          4
        ]
      ],
      [
        5,
        [
          8
        ]
      ]
    ],
    "displayAnswer": "\\text{No. The probability is }\\frac{12}{15}=\\frac{4}{5}",
    "answers": []
  },
  {
    "id": "q9",
    "instruction": "Use the passenger counts for 10 buses to answer each part.",
    "data": [
      7,
      9,
      14,
      15,
      16,
      18,
      18,
      21,
      23,
      30
    ],
    "parts": [
      {
        "id": "a",
        "label": "(a) Work out the median.",
        "answer": "17"
      },
      {
        "id": "b",
        "label": "A bus is selected at random.\n(b) What is the probability the bus has more than 20 passengers?",
        "answer": "3/10"
      },
      {
        "id": "c",
        "label": "(c) Another bus has 32 passengers. What happens to the range?",
        "answer": "Increase",
        "options": [
          "Decrease",
          "Stay the same",
          "Increase"
        ]
      }
    ],
    "calculator": "no",
    "keyValue": 14,
    "unit": "passengers",
    "rows": [
      [
        0,
        [
          7,
          9
        ]
      ],
      [
        1,
        [
          4,
          5,
          6,
          8,
          8
        ]
      ],
      [
        2,
        [
          1,
          3
        ]
      ],
      [
        3,
        [
          0
        ]
      ]
    ],
    "displayAnswer": "\\text{a: 17; b: 3/10; c: Increase}",
    "answers": []
  },
  {
    "id": "q10",
    "instruction": "12 of the students in a class sit a test.\nThe stem and leaf diagram shows their results.",
    "data": [
      60,
      61,
      63,
      65,
      68,
      72,
      72,
      76,
      79,
      80,
      87,
      96
    ],
    "parts": [
      {
        "id": "a",
        "label": "(a) Work out the median score.",
        "answer": "72"
      },
      {
        "id": "b",
        "label": "(b) A new student scores 98%. What happens to the range?",
        "answer": "Increase",
        "options": [
          "Decrease",
          "Stay the same",
          "Increase"
        ]
      },
      {
        "id": "c",
        "label": "(c) What happens to the median after adding 98%?",
        "answer": "Stay the same",
        "options": [
          "Decrease",
          "Stay the same",
          "Increase"
        ]
      }
    ],
    "calculator": "no",
    "keyValue": 60,
    "unit": "%",
    "rows": [
      [
        6,
        [
          0,
          1,
          3,
          5,
          8
        ]
      ],
      [
        7,
        [
          2,
          2,
          6,
          9
        ]
      ],
      [
        8,
        [
          0,
          7
        ]
      ],
      [
        9,
        [
          6
        ]
      ]
    ],
    "displayAnswer": "\\text{a: 72; b: Increase; c: Stay the same}",
    "answers": []
  }
];

const STORAGE_KEY = "stem_and_leaf_diagrams_quiz_v3";
const SUBMIT_URL =
  `${process.env.REACT_APP_HOMEWORK_API_URL || (process.env.NODE_ENV === "development" ? "http://localhost:5001" : "")}/api/homework/stem-and-leaf-diagrams`;

function normalize(raw) {
  let value = String(raw ?? "")
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "$1/$2")
    .replace(/\\cdot|\\times/g, "*")
    .replace(/\\div/g, "/")
    .replace(/\\,/g, "")
    .replace(/\s+/g, "")
    .replace(/[{}()]/g, "");

  return value.replace(/\*/g, "");
}

function makeSubmissionId() {
  if (
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function ResultIcon({ type, size = 28 }) {
  const isCorrect = type === "correct";
  const colour = isCorrect ? "#138a5b" : "#d64545";
  const paleColour = isCorrect ? "#e7f7ef" : "#fdecec";

  return (
    <svg
      aria-label={isCorrect ? "Correct" : "Incorrect"}
      role="img"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <circle cx="16" cy="16" r="14" fill={paleColour} stroke={colour} strokeWidth="2" />
      {isCorrect ? (
        <path
          d="M9.2 16.2l4.3 4.4 9.5-10"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M11 11l10 10M21 11L11 21"
          fill="none"
          stroke={colour}
          strokeWidth="3.2"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export default function StemAndLeafDiagramsQuiz() {
  const navigate = useNavigate();
  const questions = useMemo(() => QUESTIONS, []);

  const [studentName, setStudentName] = useState("");
  const [nameConfirmed, setNameConfirmed] = useState(false);
  const [current, setCurrent] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState("");
  const [working, setWorking] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [questionResults, setQuestionResults] = useState(Array(questions.length).fill(null));
  const [studentAnswers, setStudentAnswers] = useState(Array(questions.length).fill(""));
  const [workingAnswers, setWorkingAnswers] = useState(Array(questions.length).fill(""));
  const [attemptHistory, setAttemptHistory] = useState(Array.from({ length: questions.length }, () => []));
  const [optionalTableProgress, setOptionalTableProgress] = useState({});
  const [guidedProgress, setGuidedProgress] = useState({});
  const [reviewMode, setReviewMode] = useState(false);
  const [done, setDone] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState("idle");
  const [submissionError, setSubmissionError] = useState("");
  const [submissionId, setSubmissionId] = useState(makeSubmissionId);


  useEffect(() => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (!data) return;
      setStudentName(data.studentName || "");
      setNameConfirmed(Boolean(data.nameConfirmed));
      setCurrent(Number.isInteger(data.current) ? data.current : 0);
      if (Array.isArray(data.questionResults) && data.questionResults.length === questions.length) setQuestionResults(data.questionResults);
      if (Array.isArray(data.studentAnswers) && data.studentAnswers.length === questions.length) setStudentAnswers(data.studentAnswers);
      if (Array.isArray(data.workingAnswers) && data.workingAnswers.length === questions.length) setWorkingAnswers(data.workingAnswers);
      if (Array.isArray(data.attemptHistory) && data.attemptHistory.length === questions.length) setAttemptHistory(data.attemptHistory);
      setGuidedProgress(data.guidedProgress || {});
      setOptionalTableProgress(data.optionalTableProgress || {});
      setDone(Boolean(data.done));
      setSubmissionStatus(data.submissionStatus || "idle");
      setSubmissionError(data.submissionError || "");
      setSubmissionId(data.submissionId || makeSubmissionId());
      const index = Number.isInteger(data.current) ? data.current : 0;
      setStudentAnswer(data.studentAnswers?.[index] || "");
      setWorking(data.workingAnswers?.[index] || "");
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [questions.length]);

  const saveProgress = (override = {}) => {
    const payload = {
      studentName, nameConfirmed, current, questionResults, studentAnswers,
      workingAnswers, attemptHistory, guidedProgress, optionalTableProgress, done, submissionStatus, submissionError, submissionId, ...override,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  };

  const confirmName = (event) => {
    event.preventDefault();
    const cleanName = studentName.trim();
    if (!cleanName) return;
    setStudentName(cleanName);
    setNameConfirmed(true);
    saveProgress({ studentName: cleanName, nameConfirmed: true });
  };

  const canAdvance = questionResults[current] !== null;

  const updateGuidedProgress = (progress) => {
    const updated = { ...guidedProgress, [questions[current].id]: progress };
    setGuidedProgress(updated);
    saveProgress({ guidedProgress: updated });
  };

  const completeGuidedQuestion = (answer, progress) => {
    const updatedResults = [...questionResults];
    const updatedAnswers = [...studentAnswers];
    const updatedWorking = [...workingAnswers];
    const updatedHistory = attemptHistory.map((history) => [...history]);
    const updatedProgress = { ...guidedProgress, [questions[current].id]: progress };
    updatedResults[current] = progress.correct === false ? "incorrect" : "correct";
    updatedAnswers[current] = answer;
    updatedHistory[current].push(answer);
    updatedWorking[current] = progress.working || "";
    setQuestionResults(updatedResults);
    setStudentAnswers(updatedAnswers);
    setWorkingAnswers(updatedWorking);
    setAttemptHistory(updatedHistory);
    setStudentAnswer(answer);
    setWorking(updatedWorking[current]);
    setGuidedProgress(updatedProgress);
    saveProgress({ questionResults: updatedResults, studentAnswers: updatedAnswers, workingAnswers: updatedWorking, attemptHistory: updatedHistory, guidedProgress: updatedProgress });
  };

  const submitResults = async (results, answers, histories, workings) => {
    setSubmissionError("");
    setSubmissionStatus("sending");
    saveProgress({ done: true, questionResults: results, studentAnswers: answers, attemptHistory: histories, workingAnswers: workings, submissionStatus: "sending" });

    const payload = {
      submissionId,
      studentName: studentName.trim(),
      quizTitle: "Stem and Leaf Diagrams",
      completedAt: new Date().toISOString(),
      score: results.filter((result) => result === "correct").length,
      total: questions.length,
      questions: questions.map((question, index) => ({
        number: index + 1,
        question: question.instruction,
        instruction: question.instruction + (question.draw || question.mistakes ? " Data: " + question.data.join(", ") : ""),
        table: stemSubmissionTable(question, guidedProgress[question.id]),
        correctAnswer: stemAnswerSummary(question, Object.fromEntries(stemFields(question).map((field) => [field.id, field.answer]))),
        studentAnswer: answers[index] || "No answer",
        attempts: guidedProgress[question.id]?.attempts?.length
          ? guidedProgress[question.id].attempts : histories[index] || [],
        working: workings[index] || "",
        result: results[index] || "incorrect",
      })),
    };

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const messages = {
          404: "The results service could not be found. Please let Ryan know.",
          503: "Email delivery is not configured yet. Please let Ryan know.",
          502: "The email service could not deliver your results. Please try again later.",
        };
        throw new Error(messages[response.status] || "The results service could not accept your results. Please try again later.");
      }
      setSubmissionStatus("sent");
      saveProgress({ done: true, submissionStatus: "sent", submissionError: "" });
    } catch (error) {
      const message = error instanceof TypeError
        ? "Cannot connect to the results service. Please let Ryan know or try again later."
        : error.message;
      setSubmissionError(message);
      setSubmissionStatus("failed");
      saveProgress({ done: true, submissionStatus: "failed", submissionError: message });
    }
  };

  const nextQuestion = () => {
    setFeedback(null);
    setAttempts(0);
    const next = current + 1;

    if (next < questions.length) {
      setCurrent(next);
      setStudentAnswer(studentAnswers[next] || "");
      setWorking(workingAnswers[next] || "");
      saveProgress({ current: next });
      return;
    }

    if (reviewMode) {
      navigate("/student-dashboard");
      return;
    }

    setDone(true);
    saveProgress({ done: true });
    if (submissionStatus !== "sent") {
      submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
    }
  };

  const previousQuestion = () => {
    if (current === 0) return;
    const previous = current - 1;
    setCurrent(previous);
    setFeedback(null);
    setAttempts(attemptHistory[previous]?.length || 0);
    setStudentAnswer(studentAnswers[previous] || "");
    setWorking(workingAnswers[previous] || "");
    saveProgress({ current: previous });
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrent(0);
    setFeedback(null);
    setStudentAnswer(studentAnswers[0] || "");
    setWorking(workingAnswers[0] || "");
  };

  const retrySubmission = () => submitResults(questionResults, studentAnswers, attemptHistory, workingAnswers);
  const score = questionResults.filter((result) => result === "correct").length;

  const feedbackMessage = () => {
    const percentage = (score / questions.length) * 100;
    if (percentage === 100) return "🌟 Perfect—you got everything right!";
    if (percentage >= 80) return "🎉 Great job—you did really well!";
    if (percentage >= 50) return "👍 Good effort. Review any red questions.";
    return "💪 Keep practising. Reviewing your answers will help.";
  };

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questionResults.map((result, index) => (
        <div key={questions[index].id} style={{
          ...styles.progressDot,
          backgroundColor: result === "correct" ? "#00b894" : result === "incorrect" ? "#d63031" : "#636e72",
          border: index === current && !done ? "3px solid #f9b710" : "2px solid transparent",
        }}>
          {index + 1}
        </div>
      ))}
    </div>
  );

  if (!nameConfirmed) {
    return (
  
        <div style={styles.container}>
          <div style={styles.nameCard}>
            <h1 style={styles.heading}>Stem and Leaf Diagrams Quiz</h1>
            <p>Please enter your name before you begin. Your results will be sent to Ryan when you finish.</p>
            <form onSubmit={confirmName}>
              <label htmlFor="student-name" style={styles.label}>Your name</label>
              <input id="student-name" autoFocus autoComplete="name" maxLength={80} value={studentName}
                onChange={(event) => setStudentName(event.target.value)} style={styles.nameInput} />
              <button type="submit" disabled={!studentName.trim()} style={styles.startBtn}>Start Quiz ➡️</button>
            </form>
          </div>
        </div>
   
    );
  }

  const q = questions[current];
  const currentResult = questionResults[current];
  const completedFieldStyle =
    currentResult === "correct"
      ? styles.correctField
      : currentResult === "incorrect"
        ? styles.incorrectField
        : styles.unansweredField;

  return (
       <div style={styles.container}>
        <h1 style={styles.heading}>Stem and Leaf Diagrams</h1>
        <p style={styles.studentLabel}>Student: <strong>{studentName}</strong></p>
        {renderProgress()}

        {done && !reviewMode ? (
          <div style={styles.resultsBox}>
            <h2 style={{ fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#17324d", fontWeight: 700 }}>Score: {score} / {questions.length}</h2>
            <p style={styles.feedbackText}>{feedbackMessage()}</p>
            {submissionStatus === "sending" && <p>Sending your results to Ryan…</p>}
            {submissionStatus === "sent" && (
              <div style={{ ...styles.statusLine, ...styles.sentMessage }}>
                <ResultIcon type="correct" size={25} />
                <span>Your results have been sent to Ryan.</span>
              </div>
            )}
            {submissionStatus === "failed" && (
              <div>
                <p style={styles.errorMessage}>Your quiz is saved, but the results could not be sent.</p>
                {submissionError && <p role="alert">{submissionError}</p>}
                <button onClick={retrySubmission} style={styles.submitBtn}>Try Sending Again</button>
              </div>
            )}
            <button onClick={startReview} style={styles.reviewBtn}>🔍 Review Questions</button>
          </div>
        ) : (
          <div style={styles.card}>
            <h3 style={styles.questionHeading}>Question {current + 1} of {questions.length}</h3>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 24 }}>
              <p style={{ ...styles.instruction, flex: 1, margin: 0 }}>{q.instruction}</p>
              <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                <CalculatorIcon allowed={q.calculator === "yes"} />
              </div>
            </div>
            <StemQuestion key={q.id} question={q} progress={guidedProgress[q.id]}
              readOnly={reviewMode || canAdvance} onChange={updateGuidedProgress} onComplete={completeGuidedQuestion} />
            {reviewMode && (
              <div style={styles.reviewAnswer}>
                <div style={styles.statusLine}>
                  <ResultIcon type={questionResults[current] === "correct" ? "correct" : "incorrect"} />
                  <strong style={{ color: questionResults[current] === "correct" ? "#087f5b" : "#c0392b" }}>
                    {questionResults[current] === "correct" ? "Correct:" : "Correct answer:"}
                  </strong>
                </div>
                <BlockMath math={q.displayAnswer} />
              </div>
            )}
            {!reviewMode && feedback && <div style={styles.feedbackBox}>{feedback}</div>}
            

            {(canAdvance || reviewMode) && (
              <div>
                {current > 0 && <button onClick={previousQuestion} style={styles.secondaryBtn}>← Back</button>}
                <button onClick={nextQuestion} style={styles.nextBtn}>
                  {current + 1 < questions.length ? "Next ➡️" : reviewMode ? "Finish Review" : "Finish & Send 🎉"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
  );
}

const styles = {
  container: { padding: "2rem", fontFamily: "'Fredoka', cursive", backgroundColor: "#eaf4fc", minHeight: "100vh" },
  heading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "clamp(1.6rem, 3vw, 2.25rem)", fontWeight: 700, lineHeight: 1.25, letterSpacing: "-0.035em", color: "#17324d", textAlign: "center", maxWidth: 760, margin: "0 auto 1.5rem", paddingBottom: "1rem", borderBottom: "3px solid #b8d4e8" },
  questionHeading: { fontFamily: "Inter, 'Segoe UI', sans-serif", fontSize: "1rem", fontWeight: 600, letterSpacing: "0.025em", color: "#475569", margin: "0 0 1.25rem" },
  studentLabel: { textAlign: "center", marginBottom: "1rem" },
  card: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 700, margin: "1rem auto 0" },
  nameCard: { backgroundColor: "#fff", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", maxWidth: 520, margin: "4rem auto" },
  nameInput: { display: "block", width: "100%", boxSizing: "border-box", padding: "0.75rem", margin: "0.5rem 0 1rem", border: "1px solid #aaa", borderRadius: 8, fontSize: "1.1rem" },
  startBtn: { padding: "0.7rem 1.3rem", backgroundColor: "#6c5ce7", color: "white", fontWeight: "bold", border: 0, borderRadius: 7, cursor: "pointer" },
  calculatorIndicator: { marginBottom: 12, display: "flex", justifyContent: "flex-end" },
  instruction: { whiteSpace: "pre-line", fontWeight: 700, fontSize: "1.05rem", marginBottom: 24 },
  label: { fontFamily: "Inter, 'Segoe UI', sans-serif", color: "#334155", fontWeight: 600, marginTop: 10, display: "block" },
  mathInput: { display: "block", width: "100%", boxSizing: "border-box", fontSize: "1.25rem", padding: "0.65rem", borderRadius: 8, border: "1px solid #ccc", marginTop: 8 },
  workingInput: { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #ccc", padding: "0.75rem", fontSize: "1rem", fontFamily: "'Fredoka', cursive", marginTop: 8, marginBottom: 20 },
  unansweredField: { backgroundColor: "#fff", border: "1px solid #ccc" },
  correctField: { backgroundColor: "#e8f8ee", border: "2px solid #42a96b" },
  incorrectField: { backgroundColor: "#fdeaea", border: "2px solid #d95858" },
  feedbackBox: { marginTop: "1rem" },
  statusLine: { display: "flex", alignItems: "center", gap: 10 },
  reviewAnswer: { marginTop: "1rem", padding: "1rem", backgroundColor: "#f5f6fa", borderRadius: 8 },
  submitBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#0984e3", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  nextBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#00b894", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
  secondaryBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer", marginRight: "0.75rem" },
  progressWrapper: { display: "flex", justifyContent: "center", gap: 10, marginBottom: "1.5rem", flexWrap: "wrap" },
  progressDot: { width: 36, height: 36, borderRadius: "50%", display: "flex", justifyContent: "center", alignItems: "center", fontWeight: "bold", color: "white" },
  resultsBox: { textAlign: "center", backgroundColor: "white", maxWidth: 700, margin: "2rem auto", padding: "2rem", borderRadius: 12, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" },
  feedbackText: { fontSize: "1.25rem", margin: "1rem 0" },
  sentMessage: { color: "#087f5b", fontWeight: 700, justifyContent: "center", margin: "1rem 0" },
  errorMessage: { color: "#c0392b", fontWeight: 700 },
  reviewBtn: { marginTop: "1rem", padding: "0.6rem 1.2rem", backgroundColor: "#636e72", color: "white", fontWeight: "bold", border: 0, borderRadius: 6, cursor: "pointer" },
};


function CalculatorIcon({ allowed }) {
  const label = allowed ? "Calculator allowed" : "No calculator";

  return (
    <svg role="img" aria-label={label} width={56} height={40} viewBox="0 0 56 40"
      style={{ display: "block" }}>
      <title>{label}</title>
      <rect x="5" y="3" width="27" height="34" rx="4" fill="#f1f5f9" stroke="#334155" strokeWidth="2" />
      <rect x="10" y="8" width="17" height="7" rx="1" fill="#cbd5e1" stroke="#334155" />
      <path d="M11 21h2m5 0h2m5 0h2M11 27h2m5 0h2m5 0h2M11 32h2m5 0h2m5 0h2"
        stroke="#334155" strokeWidth="3" strokeLinecap="round" />
      {allowed ? (
        <path d="M37 22l5 5 9-12" fill="none" stroke="#138a5b" strokeWidth="4"
          strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M5 5l27 30M32 5L5 35" fill="none" stroke="#d64545" strokeWidth="4"
          strokeLinecap="round" />
      )}
    </svg>
  );
}

function stemFields(question) {
  const fields = [];
  const offset = question.guided ? 1 : 0;
  if (question.guided) [...question.data].sort((a, b) => a - b).forEach((value, index) => fields.push({ id: `o${index}`, label: `Ordered number ${index + 1}`, answer: String(value), stage: 0 }));
  if (question.draw) {
    question.rows.forEach(([stem, leaves], row) => {
      fields.push({ id: `s${row}`, label: `Stem in row ${row + 1}`, answer: String(stem), stage: offset });
      leaves.forEach((leaf, column) => fields.push({ id: `l${row}_${column}`, label: `Leaf ${column + 1} in row ${row + 1}`, answer: String(leaf), stage: 1 + offset }));
    });
    if (!question.keyProvided) fields.push({ id: "key", label: "Number represented by the key", answer: String(question.keyValue), stage: 2 + offset });
  }
  fields.push(...question.parts.map((part) => ({ ...part, stage: 0 })));
  return fields;
}

function numericAnswer(value) {
  const cleaned = String(value ?? "").trim().replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "$1/$2").replace(/\s|%/g, "");
  if (/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(cleaned)) return Number(cleaned);
  if (/^[+-]?\d+\/[+-]?\d+$/.test(cleaned)) {
    const [a, b] = cleaned.split("/").map(Number);
    return b !== 0 ? a / b : NaN;
  }
  return NaN;
}

function stemAnswerMatches(field, values, question) {
  const value = String(values[field.id] ?? "").trim();
  if (!value) return false;
  if (question.mistakes) return ["order", "missing"].includes(value) && (field.id !== "b" || value !== values.a);
  if (field.explanation) {
    const text = value.toLowerCase().replace(/⁄/g, "/");
    if (/\b(?:not|isn't|isnt)\s+(?:equal to\s+)?(?:4\s*\/\s*5|0\.8|80\s*%)/.test(text)) return false;
    const fractions = [...text.matchAll(/(\d+)\s*(?:\/|out of|over)\s*(\d+)/g)];
    return fractions.some((match) => Number(match[2]) > 0 && Math.abs(Number(match[1]) / Number(match[2]) - 0.8) < 1e-9)
      || /(?:^|[^\d.])0\.80*(?![\d.])|\b80(?:\.0+)?\s*(?:%|percent\b|per cent\b)|\bfour\s+(?:fifths|out of five)\b|\btwelve\s+(?:out of|over)\s+fifteen\b/.test(text);
  }
  if (field.options) return value === field.answer;
  return Math.abs(numericAnswer(value) - numericAnswer(field.answer)) < 1e-9;
}

function stemAnswerSummary(question, values) {
  const sections = [];
  if (question.draw) {
    sections.push(question.rows.map(([, leaves], row) => `${values[`s${row}`] || "Blank"} | ${leaves.map((_, column) => values[`l${row}_${column}`] || "_").join(" ")}`).join("; "));
    if (!question.keyProvided) sections.push(`Key number: ${values.key || "Blank"} ${question.unit || ""}`);
  }
  question.parts.forEach((part) => {
    const value = values[part.id];
    const option = part.options?.find((item) => (typeof item === "string" ? item : item.value) === value);
    sections.push(`${part.label}: ${question.mistakes ? ({ order: "The leaves are not ordered", missing: "One of the 13s is missing", stems: "The stems are in the wrong order", key: "The key is incorrect" }[value] || "Blank") : typeof option === "object" ? option.label : value || "Blank"}`);
  });
  return sections.join("; ");
}

function stemSubmissionTable(question, progress = {}) {
  const values = progress.values || {}, status = progress.status || {};
  const combinedStatus = (keys) => keys.every((key) => status[key] === "correct") ? "correct" : keys.some((key) => status[key] === "incorrect") ? "incorrect" : undefined;
  return {
    valueLabel: "Stem", frequencyLabel: "Leaf", cumulative: false, showTotal: false,
    keyLabel: `Key: ${Math.floor(question.keyValue / 10)} | ${question.keyValue % 10} means ${question.draw && !question.keyProvided ? values.key || "Blank" : question.keyValue} ${question.unit || ""}`,
    rows: question.rows.map(([stem, leaves], row) => question.draw ? [
      { value: values[`s${row}`] ?? "", status: status[`s${row}`] },
      { value: leaves.map((_, column) => values[`l${row}_${column}`] || "_ ").join(" "), status: combinedStatus(leaves.map((_, column) => `l${row}_${column}`)) },
    ] : [stem, leaves.join(" ")]),
  };
}

function StemQuestion({ question, progress, readOnly, onChange, onComplete }) {
  const [activeHint, setActiveHint] = useState(null);
  const hintDialog = useRef(null);
  const reduceMotion = useReducedMotion();
  const state = progress || { step: 0, values: {}, status: {}, checks: 0, attempts: [], working: "" };
  const fields = stemFields(question);
  const hints = [
    "Start with the smallest number and work up to the biggest. Keep every repeated number: each one needs its own box.",
    "The stem is everything except the final digit. Put the stems in increasing order, using one row for each stem.",
    "The leaf is the final digit. Put each leaf next to its stem, from smallest to largest. Keep repeated values: each one needs its own leaf.",
    "A key tells the reader how to join a stem and a leaf. Combine the shown stem and leaf to make the original number, then include its units.",
  ];
  useEffect(() => {
    const dialog = hintDialog.current;
    if (!dialog) return;
    if (activeHint !== null && !dialog.open) dialog.showModal();
    if (activeHint === null && dialog.open) dialog.close();
  }, [activeHint]);
  const update = (id, value) => onChange({ ...state, values: { ...state.values, [id]: value }, status: { ...state.status, [id]: undefined }, message: "" });
  const fieldStyle = (id) => ({ ...guidedStyles.input, ...(state.status[id] === "correct" ? guidedStyles.correct : state.status[id] === "incorrect" ? guidedStyles.incorrect : state.status[id] === "revealed" ? { backgroundColor: "#eff6ff", borderColor: "#60a5fa", color: "#1d4ed8" } : {}) });
  const input = (id) => {
    const field = fields.find((item) => item.id === id);
    const locked = readOnly || state.status[id] === "correct" || (question.guided && field.stage < state.step);
    if (question.id === "q9" && id === "b") return <math-field class="stem-fraction-input" virtual-keyboard-mode="off" aria-label={field.label} readOnly={locked} value={state.values[id] || ""} onInput={(event) => !locked && update(id, event.target.value)} style={{ ...fieldStyle(id), minHeight: 58 }} />;
    if (field.explanation) return <textarea aria-label={field.label} rows={4} value={state.values[id] || ""} readOnly={locked} onChange={(event) => { const value = event.target.value; onChange({ ...state, values: { ...state.values, [id]: value }, working: value, status: { ...state.status, [id]: undefined }, message: "" }); }} aria-invalid={state.status[id] === "incorrect"} style={{ ...styles.workingInput, ...fieldStyle(id), width: "100%", textAlign: "left" }} />;
    if (field.options) return <select aria-label={field.label} value={state.values[id] || ""} disabled={locked} onChange={(event) => update(id, event.target.value)} aria-invalid={state.status[id] === "incorrect"} style={{ ...fieldStyle(id), width: "100%", textAlign: "left" }}>
      <option value="">Choose an answer</option>
      {field.options.map((option, index) => <option key={option} value={question.mistakes ? ["order", "missing", "stems", "key"][index] : option}>{option}</option>)}
    </select>;
    return <input aria-label={field.label} type="text" inputMode={id.startsWith("l") || id.startsWith("s") ? "numeric" : "decimal"} maxLength={id.startsWith("l") ? 1 : 40} value={state.values[id] || ""} readOnly={locked} onChange={(event) => update(id, event.target.value)} aria-invalid={state.status[id] === "incorrect"} style={{ ...fieldStyle(id), ...(id.startsWith("l") ? { width: 42, padding: "8px 2px" } : {}) }} />;
  };
  const check = () => {
    const active = question.guided ? fields.filter((field) => field.stage === state.step) : fields;
    const status = { ...state.status };
    active.forEach((field) => { status[field.id] = stemAnswerMatches(field, state.values, question) ? "correct" : "incorrect"; });
    const correct = active.every((field) => status[field.id] === "correct");
    void playQuizFeedback(correct);
    const count = (state.checks || 0) + 1;
    const attempt = active.map((field) => `${field.label}: ${state.values[field.id] || "Blank"} (${status[field.id]})`).join("; ") + (state.working ? ` | Working: ${state.working}` : "");
    const next = { ...state, status, checks: count, attempts: [...(state.attempts || []), attempt], step: question.guided && correct ? state.step + 1 : state.step,
      message: correct ? "Correct!" : question.guided ? "Check the red boxes and try again. Every box must be green before you continue." : count < 3 ? `Try again. Attempts left: ${3 - count}` : "Review the correct answers below.", correct };
    if (!question.guided && !correct && count >= 3) {
      next.values = { ...state.values };
      fields.forEach((field) => { if (status[field.id] === "incorrect") { next.values[field.id] = field.answer; next.status[field.id] = "revealed"; } });
      next.message = "Correct answers are shown in blue. You can move on now.";
    }
    onChange(next);
    if (question.guided ? correct && state.step === 3 : correct || count >= 3) onComplete(stemAnswerSummary(question, state.values), next);
  };
  const heading = (stage, label) => <div style={guidedStyles.stepHeading}>
    <h4 style={{ margin: 0 }}>{label}</h4>
    <button type="button" className="median-hint-button" title="Show a hint" aria-label={`Hint: ${label}`} onClick={() => setActiveHint(stage)} style={guidedStyles.hintButton}>
      <svg aria-hidden="true" width="24" height="24" viewBox="0 -1 24 24" style={{ display: "block" }}><path d="M9 17h6v-2c0-2 3-3 3-7a6 6 0 00-12 0c0 4 3 5 3 7z" fill="#fde68a" stroke="currentColor" strokeWidth="1.7" /><path d="M9 20h6m-5 2h4M12 0v1M2 8h2m16 0h2M4 2l2 2m12 0l2-2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
    </button>
  </div>;
  return <div>
    {(question.draw || question.mistakes) && <div aria-label="Given data" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>{question.data.map((value, index) => <span key={index} style={{ padding: "8px 12px", background: "#eff6ff", borderRadius: 8, fontSize: "1.2rem" }}>{value}</span>)}</div>}
    <section style={{ ...guidedStyles.section, border: "none", padding: "8px 0" }}>
      {question.guided && heading(Math.min(state.step, 3), ["1. Order the numbers from smallest to biggest", "2. Fill in the stems", "3. Fill in the ordered leaves", "4. Complete the key", "Diagram complete"][state.step])}
      {question.guided && <div aria-label="Ordered numbers" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>{question.data.map((_, index) => <React.Fragment key={index}>{input(`o${index}`)}</React.Fragment>)}</div>}
      {(!question.guided || state.step >= 1) && <>
      <div style={{ overflowX: "auto" }}><table aria-label="Stem and leaf diagram" style={guidedStyles.table}>
        <tbody>{question.rows.map(([stem, leaves], row) => <tr key={row} style={{ borderBottom: row < question.rows.length - 1 ? "1px solid #334155" : "none" }}>
          <td style={{ ...guidedStyles.cell, width: 95, borderRight: "none", boxShadow: "inset -1px 0 0 #334155" }}>{question.draw ? input(`s${row}`) : stem}</td>
          <td style={{ ...guidedStyles.cell, textAlign: "left" }}><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {question.draw ? (!question.guided || state.step >= 2 ? leaves.map((_, column) => <React.Fragment key={column}>{input(`l${row}_${column}`)}</React.Fragment>) : <span style={{ color: "#64748b", fontSize: "0.9rem" }}>Complete the stems first</span>) : leaves.map((leaf, column) => <span key={column} style={{ minWidth: 25 }}>{leaf}</span>)}
          </div></td>
        </tr>)}</tbody>
      </table></div>
      {(!question.guided || state.step >= 3) && <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}><strong>Key:</strong><span style={{ fontSize: "1.2rem" }}>{Math.floor(question.keyValue / 10)} | {question.keyValue % 10} means</span>{question.draw && !question.keyProvided ? input("key") : <strong>{question.keyValue}</strong>}<span>{question.unit || ""}</span></div>}
      </>}
      {question.guided && !readOnly && state.step < 4 && <button type="button" onClick={check} style={guidedStyles.button}>Check {["order", "stems", "leaves", "key"][state.step]}</button>}
    </section>
    {question.id === "q8" && <div style={{ ...styles.instruction, margin: "24px 0", lineHeight: 1.7 }}>
      <div>If a student took less than 40 minutes, they win a prize.</div>
      <div>One of the students is picked at random.</div>
      <div>Thea says that the probability that the student won a prize is <span style={{ display: "inline-flex", flexDirection: "column", verticalAlign: "middle", textAlign: "center", lineHeight: 1.15, margin: "0 4px" }} aria-label="two thirds"><span style={{ borderBottom: "1px solid currentColor", padding: "0 4px 2px" }}>2</span><span style={{ paddingTop: 2 }}>3</span></span></div>
      <div style={{ marginTop: 16 }}>Is Thea correct?</div>
      <div>Explain your answer.</div>
    </div>}
    {question.parts.map((part) => <section key={part.id} style={{ marginBottom: 20 }}><label style={{ ...styles.label, whiteSpace: "pre-line" }}>{part.label}</label><div style={{ marginTop: 8, ...(question.id === "q10" && part.id === "a" ? { display: "flex", alignItems: "center", gap: 8 } : {}) }}>{input(part.id)}{question.id === "q10" && part.id === "a" && <span style={{ fontSize: "1.1rem" }}>%</span>}</div></section>)}
    {question.id !== "q8" && <>
    <label style={styles.label}>{question.id === "q8" ? "Explain your answer / show your working:" : "Show your working:"}</label>
    <textarea rows={4} value={state.working || ""} readOnly={readOnly} onChange={(event) => onChange({ ...state, working: event.target.value })} style={styles.workingInput} />
    </>}
    {!question.guided && !readOnly && <button type="button" onClick={check} style={styles.submitBtn}>Check Answer</button>}
    <p role="status" aria-live="polite" style={{ color: state.message?.startsWith("Correct") ? "#087f5b" : "#c0392b" }}>{state.message}</p>
    <style>{`.stem-fraction-input::part(virtual-keyboard-toggle), .stem-fraction-input::part(menu-toggle) { display: none; } .median-hint-dialog::backdrop { background: rgba(30,27,75,.38); } .median-hint-button { color:#92400e; background:#fffbeb; transition:background-color 150ms ease; } .median-hint-button:hover,.median-hint-button:focus-visible { background:#fde68a; }`}</style>
    <dialog ref={hintDialog} className="median-hint-dialog" aria-labelledby="stem-hint-title" onCancel={() => setActiveHint(null)} onClose={() => setActiveHint(null)} onClick={(event) => { if (event.target === event.currentTarget) setActiveHint(null); }} style={guidedStyles.hintDialog}>
      {activeHint !== null && <motion.div key={activeHint} initial={reduceMotion ? false : { scale: .45, opacity: 0, rotate: -5 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 320, damping: 19 }} style={guidedStyles.hintBubble}><h3 id="stem-hint-title" style={{ color: "#6d28d9", margin: "0 0 12px" }}>A little hint!</h3><p style={{ fontSize: "1.1rem", lineHeight: 1.6 }}>{hints[activeHint]}</p><button type="button" autoFocus onClick={() => setActiveHint(null)} style={guidedStyles.button}>Got it!</button><span aria-hidden="true" style={guidedStyles.bubbleTail} /></motion.div>}
    </dialog>
  </div>;
}

const guidedStyles = {
  stepHeading: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 },
  hintButton: { display: "grid", placeItems: "center", padding: 0, boxSizing: "border-box", flexShrink: 0, width: 40, height: 40, borderRadius: "50%", border: "2px solid #f59e0b", cursor: "pointer", boxShadow: "0 3px 0 #fcd34d" },
  hintDialog: { border: 0, padding: 36, background: "transparent", maxWidth: 540, width: "calc(100% - 24px)", boxSizing: "border-box", overflow: "visible", fontFamily: "'Fredoka', cursive" },
  hintBubble: { position: "relative", padding: 28, border: "3px solid #7c3aed", borderRadius: "32px 32px 32px 8px", background: "#fffbeb", color: "#334155", boxShadow: "0 0 20px #c4b5fd, 0 0 44px rgba(167,139,250,0.7), 0 12px 0 rgba(109,40,217,0.18)" },
  bubbleTail: { position: "absolute", bottom: -17, left: 28, width: 26, height: 26, background: "#fffbeb", borderRight: "3px solid #7c3aed", borderBottom: "3px solid #7c3aed", transform: "rotate(45deg)" },
  section: { borderWidth: 2, borderStyle: "solid", borderColor: "#cbd5e1", borderRadius: 8, padding: 16, marginBottom: 16 },
  correct: { backgroundColor: "#e8f8ee", borderColor: "#42a96b" },
  incorrect: { backgroundColor: "#fdeaea", borderColor: "#d95858" },
  table: { borderCollapse: "collapse", width: "100%", border: "none", backgroundColor: "transparent", fontSize: "1.15rem" },
  cell: { border: "none", backgroundColor: "transparent", padding: "14px 12px", textAlign: "center" },
  input: { width: 70, maxWidth: "100%", boxSizing: "border-box", padding: 8, borderWidth: 2, borderStyle: "solid", borderColor: "#94a3b8", borderRadius: 5, fontSize: "1.1rem", textAlign: "center" },
  button: { display: "block", marginTop: 16, padding: "10px 16px", backgroundColor: "#0984e3", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontWeight: "bold" },
  success: { color: "#087f5b", fontWeight: "bold" },
};


// Generate short feedback tones locally, without loading audio files.
let quizAudioContext;
async function playQuizFeedback(correct) {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    if (!quizAudioContext || quizAudioContext.state === "closed") quizAudioContext = new AudioContextClass();
    if (quizAudioContext.state === "suspended") await quizAudioContext.resume();
    const context = quizAudioContext;
    const start = context.currentTime;
    const oscillator = context.createOscillator();
    const volume = context.createGain();
    oscillator.type = correct ? "sine" : "triangle";
    oscillator.frequency.setValueAtTime(correct ? 880 : 240, start);
    if (correct) oscillator.frequency.setValueAtTime(1174.66, start + 0.07);
    else {
      oscillator.frequency.exponentialRampToValueAtTime(170, start + 0.1);
      oscillator.frequency.setValueAtTime(210, start + 0.14);
      oscillator.frequency.exponentialRampToValueAtTime(130, start + 0.28);
    }
    const duration = correct ? 0.2 : 0.3;
    volume.gain.setValueAtTime(0.0001, start);
    volume.gain.exponentialRampToValueAtTime(correct ? 0.15 : 0.14, start + 0.008);
    if (!correct) {
      volume.gain.exponentialRampToValueAtTime(0.0001, start + 0.11);
      volume.gain.setValueAtTime(0.0001, start + 0.14);
      volume.gain.exponentialRampToValueAtTime(0.14, start + 0.15);
    }
    volume.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(volume);
    volume.connect(context.destination);
    oscillator.onended = () => { oscillator.disconnect(); volume.disconnect(); };
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  } catch {
    // Audio availability should never interrupt answer checking.
  }
}
